import React, { FC, useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { AnimatePresence, motion } from "framer-motion"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import logoText from "@assets/logo_with_text_white.png?url"
import productCGUrl from "@assets/icons/product-cg.png?url"
import productWebsiteUrl from "@assets/icons/product-website.png?url"
import productStoreUrl from "@assets/icons/product-store.png?url"
import productStudioManagerUrl from "@assets/icons/product-studio-manager.png?url"
import productMobileUrl from "@assets/icons/product-mobile.png?url"
import productEditorUrl from "@assets/icons/product-editor.png?url"
import { pixiesetSubdomains, pixiesetURLs } from "@/utils/pixieset-urls"
import { isElectronApp } from "@/utils/electron"

const Product: FC<{
	iconSrc: string
	title: string
	description: string
	href: string
	onClick?: React.MouseEventHandler
}> = ({ iconSrc, title, description, href, onClick }) => (
	<li className="h-[72px] w-full px-5 hover:bg-base-700/30">
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="block size-full cursor-default py-[15px]"
			onClick={onClick}
		>
			<div className="flex content-start items-start">
				<div className="mr-[14px] flex flex-col">
					<img src={iconSrc} className="mr-px mt-px size-8" />
				</div>
				<div className="flex flex-col">
					<span className="text-[15px] font-semibold text-base-50">{title}</span>
					<span className="mt-[3px] text-[11px] font-semibold text-base-400">
						{description}
					</span>
				</div>
			</div>
		</a>
	</li>
)

const ProductTitle: FC<{ interactive?: boolean }> = ({ interactive }) => {
	const { t } = useTranslation("components")
	return (
		<span className="flex items-center gap-[6px] font-proxima text-[14px] font-semibold">
			<img src={productEditorUrl} className="size-[18px] object-contain" />
			{t("productSwitcher.photoEditor")}
			{interactive && (
				<svg
					className="size-2 text-base-400"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M19 9l-7 7-7-7" // i18n-ignore: SVG path
					/>
				</svg>
			)}
		</span>
	)
}

const ProductDropdown: FC = () => {
	const { t } = useTranslation("components")
	const [isOpen, setIsOpen] = useState(false)
	const dropdownRef = useRef<HTMLDivElement>(null)

	const toggleDropdown = () => {
		setIsOpen(!isOpen)
	}

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside)
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside)
		}
	}, [isOpen])

	return (
		<div className="relative pt-px leading-none" ref={dropdownRef}>
			<button onClick={toggleDropdown}>
				<ProductTitle interactive />
			</button>

			<AnimatePresence>
				{isOpen && (
					<motion.div
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						className="absolute left-0 top-full z-30 mt-2 rounded-md border border-base-700 bg-base-800 font-proxima"
					>
						<div className="w-[310px]">
							<ul className="mt-[10px] w-full leading-snug">
								<Product
									title={t("productSwitcher.clientGallery")}
									description={t("productSwitcher.descClientGallery")}
									iconSrc={productCGUrl}
									href={pixiesetURLs.galleries.collections}
								/>
								<Product
									title={t("productSwitcher.website")}
									description={t("productSwitcher.descWebsite")}
									iconSrc={productWebsiteUrl}
									href={pixiesetSubdomains.website}
								/>
								<Product
									title={t("productSwitcher.store")}
									description={t("productSwitcher.descStore")}
									iconSrc={productStoreUrl}
									href={pixiesetURLs.gallery.store}
								/>
								<Product
									title={t("productSwitcher.studioManager")}
									description={t("productSwitcher.descStudioManager")}
									iconSrc={productStudioManagerUrl}
									href={pixiesetSubdomains.studio}
								/>
								<Product
									title={t("productSwitcher.mobileGalleryApp")}
									description={t("productSwitcher.descMobileGallery")}
									iconSrc={productMobileUrl}
									href={pixiesetSubdomains.mobileapp}
								/>
								<Product
									title={t("productSwitcher.photoEditor")}
									description={t("productSwitcher.descPhotoEditor")}
									iconSrc={productEditorUrl}
									href="/"
									onClick={(e: React.MouseEvent) => {
										setIsOpen(false)
										e.preventDefault()
									}}
								/>
								<li className="mt-[10px] size-full bg-base-700 px-5 text-center">
									<a
										href={pixiesetURLs.accounts.dashboard}
										target="_blank"
										rel="noopener noreferrer"
										className="flex size-full cursor-default items-center justify-center py-[15px]"
									>
										<span className="flex items-center gap-1 text-[13px] font-semibold text-black dark:text-white">
											<svg
												className="mr-[5px] size-4 text-base-400"
												aria-hidden="true"
												focusable="false"
												role="img"
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 512 512"
											>
												<path
													className=""
													fill="currentColor"
													d="M224 80c0-26.5-21.5-48-48-48L80 32C53.5 32 32 53.5 32 80l0 96c0 26.5 21.5 48 48 48l96 0c26.5 0 48-21.5 48-48l0-96zm0 256c0-26.5-21.5-48-48-48l-96 0c-26.5 0-48 21.5-48 48l0 96c0 26.5 21.5 48 48 48l96 0c26.5 0 48-21.5 48-48l0-96zM288 80l0 96c0 26.5 21.5 48 48 48l96 0c26.5 0 48-21.5 48-48l0-96c0-26.5-21.5-48-48-48l-96 0c-26.5 0-48 21.5-48 48zM480 336c0-26.5-21.5-48-48-48l-96 0c-26.5 0-48 21.5-48 48l0 96c0 26.5 21.5 48 48 48l96 0c26.5 0 48-21.5 48-48l0-96z" // i18n-ignore: SVG path
												></path>
											</svg>{" "}
											{t("productSwitcher.viewDashboard")}
										</span>
									</a>
								</li>
							</ul>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}

export const ProductSwitcher: FC = () => {
	const { t } = useTranslation("components")
	if (!isPixiesetAuthEnabled()) {
		return (
			<img
				className="mr-2 h-4 object-contain invert dark:invert-0"
				src={logoText}
				alt={t("productSwitcher.altLogo")}
			/>
		)
	}

	if (isElectronApp) {
		return <ProductTitle />
	}

	return <ProductDropdown />
}
